import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { Button, Tag, Divider, Spin, Result, message } from "antd";
import {
  FileTextOutlined,
  CalendarOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import CheckoutForm from "./stripe/CheckoutForm";
import CommonCard from "./global/card";
import { useGenericQuery } from "../queries/use-generic-query";
import { useGenericMutation } from "../queries/use-generic-mutation";
import { QUERY_KEYS } from "../lib/query-constants";
import { getMonthName } from "../utils/functions";
import { MSG_INVOICES } from "../utils/message";

const EnhancedPaymentInvoice = () => {
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams(window.location.search);
  const invoiceId = queryParams.get("invoice");
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [invoiceFor, setInvoiceFor] = useState("");
  const [cardToken, setCardToken] = useState(null);

  const { data: invoiceDetail, isLoading: invoiceLoading } = useGenericQuery({
    endpoint: `invoice/detail?invoiceId=${invoiceId}`,
    queryKey: QUERY_KEYS.invoices?.item({ id: invoiceId }) || ["invoice", invoiceId],
    enabled: !!invoiceId,
  });

  const payInvoiceMutation = useGenericMutation({
    endpoint: `/payment/invoice`,
    method: "post",
    onSuccess: () => {
      message.success(MSG_INVOICES.PAID);
      setPaymentSuccess(true);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.invoices?.lists() || ["invoices"],
      });
    },
  });

  useEffect(() => {
    if (invoiceDetail) {
      const invoiceDuration = getMonthName(invoiceDetail?.invoiceMonth) + ', ' + invoiceDetail?.invoiceYear;
      setInvoiceFor(invoiceDuration);
    }
  }, [invoiceDetail]);

  const handleForm = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    const card = elements.getElement(CardElement);
    const result = await stripe.createToken(card);
    if (result.error) {
      console.log(result.error.message);
    } else {
      setCardToken(result.token);
      try {
        await payInvoiceMutation.mutateAsync({
          cardToken: result.token,
          amount: invoiceDetail?.totalAmount,
          invoiceId,
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  if (paymentSuccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          title="Payment Successful!"
          subTitle={`Invoice for ${invoiceFor} has been paid successfully.`}
          extra={[
            <Button type="primary" size="large" onClick={() => history.push("/invoices")}>
              Go to Invoices
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <CommonCard
      title="Pay Invoice"
      className="max-w-4xl mx-auto"
    >
      <Spin spinning={invoiceLoading}>
        {invoiceDetail && (
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center">
                  <FileTextOutlined className="text-primary text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Invoice Month</p>
                  <p className="text-lg font-semibold text-gray-800">{invoiceFor}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center">
                  <DollarOutlined className="text-green-500 text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Amount Due</p>
                  <p className="text-lg font-bold text-green-600">{invoiceDetail?.totalAmount} $</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center">
                  <CalendarOutlined className="text-gray-500 text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">Status</p>
                  <Tag color="error" className="px-3 py-1 rounded-full border-none font-medium m-0">
                    Unpaid
                  </Tag>
                </div>
              </div>
            </div>
          </div>

          <Divider className="my-6" />

          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCardOutlined className="text-primary" />
              Enter Card Details
            </h3>
            
            <form onSubmit={handleForm}>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <CheckoutForm />
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  type="default"
                  size="large"
                  onClick={() => history.push("/invoices")}
                  className="flex-1 h-12 hover:!border-primary hover:!text-primary"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  icon={<CreditCardOutlined />}
                  className="flex-1 h-12 !bg-primary hover:!bg-primary/80"
                  loading={payInvoiceMutation.isPending}
                >
                  Pay Now {invoiceDetail?.totalAmount} $
                </Button>
              </div>
            </form>
          </div>
        </div>
        )}
      </Spin>
    </CommonCard>
  );
};

export default EnhancedPaymentInvoice;
